import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Minus, Plus, Save, X } from "lucide-react";
import { useId, useState } from "react";

interface PersonaDraft {
  name: string;
  summary: string;
  description: string;
}

interface PersonasEditorProps {
  initial: PersonaDraft[];
  onSave: (personas: PersonaDraft[]) => void;
  onCancel: () => void;
  saving: boolean;
}

interface KeyedPersona extends PersonaDraft {
  _key: string;
}

const EMPTY_PERSONA: PersonaDraft = {
  name: "",
  summary: "",
  description: "",
};

export function PersonasEditor({ initial, onSave, onCancel, saving }: PersonasEditorProps) {
  const keyPrefix = useId();
  const [nextKey, setNextKey] = useState(initial.length);
  const [personas, setPersonas] = useState<KeyedPersona[]>(
    (initial.length > 0 ? initial : [EMPTY_PERSONA]).map((p, i) => ({
      ...p,
      _key: `${keyPrefix}-${i}`,
    })),
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const addPersona = () => {
    setPersonas([...personas, { ...EMPTY_PERSONA, _key: `${keyPrefix}-${nextKey}` }]);
    setNextKey((n) => n + 1);
  };

  const removePersona = (index: number) => setPersonas(personas.filter((_, i) => i !== index));

  const updatePersona = (index: number, field: keyof PersonaDraft, value: string) =>
    setPersonas(personas.map((p, i) => (i === index ? { ...p, [field]: value } : p)));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = personas.findIndex((p) => p._key === active.id);
    const newIndex = personas.findIndex((p) => p._key === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    setPersonas(arrayMove(personas, oldIndex, newIndex));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(personas.filter((p) => p.name.trim() !== "").map(({ _key, ...rest }) => rest));
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-3 rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-text-secondary">Personas</h3>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs text-text-muted hover:bg-card-hover"
          >
            <X size={12} /> Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-1 rounded bg-accent px-3 py-1 text-xs font-medium text-white hover:bg-accent/90 disabled:opacity-50"
          >
            <Save size={12} /> {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={personas.map((p) => p._key)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {personas.map((persona, i) => (
              <SortablePersonaRow
                key={persona._key}
                id={persona._key}
                persona={persona}
                index={i}
                canRemove={personas.length > 1}
                onChange={updatePersona}
                onRemove={removePersona}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <button
        type="button"
        onClick={addPersona}
        className="flex items-center gap-1 text-xs text-accent hover:text-accent/80"
      >
        <Plus size={12} /> Add persona
      </button>
    </form>
  );
}

interface SortablePersonaRowProps {
  id: string;
  persona: KeyedPersona;
  index: number;
  canRemove: boolean;
  onChange: (index: number, field: keyof PersonaDraft, value: string) => void;
  onRemove: (index: number) => void;
}

function SortablePersonaRow({
  id,
  persona,
  index,
  canRemove,
  onChange,
  onRemove,
}: SortablePersonaRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-2 rounded border border-border bg-card p-2"
    >
      <button
        type="button"
        aria-label={`Reorder persona ${index + 1}`}
        className="mt-1.5 flex-shrink-0 cursor-grab touch-none rounded p-0.5 text-text-faint hover:text-text-secondary focus:outline-none focus:ring-1 focus:ring-accent active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <GripVertical size={16} />
      </button>
      <div className="flex-1 space-y-1.5">
        <input
          type="text"
          value={persona.name}
          onChange={(e) => onChange(index, "name", e.target.value)}
          className="w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          placeholder="Persona name (e.g. 'Scaling founder-CTO')"
        />
        <input
          type="text"
          value={persona.summary}
          onChange={(e) => onChange(index, "summary", e.target.value)}
          className="w-full rounded border border-input-border bg-card px-2 py-1 text-xs text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          placeholder="One-line summary"
        />
        <textarea
          value={persona.description}
          onChange={(e) => onChange(index, "description", e.target.value)}
          className="w-full rounded border border-input-border bg-card px-2 py-1 text-xs text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          placeholder="Background, career trajectory, strengths, why they fit"
          rows={4}
        />
      </div>
      {canRemove && (
        <button
          type="button"
          onClick={() => onRemove(index)}
          aria-label={`Remove persona ${index + 1}`}
          className="mt-1.5 rounded p-1 text-text-faint hover:bg-card-hover hover:text-text-secondary"
        >
          <Minus size={14} />
        </button>
      )}
    </div>
  );
}
